import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2d3748;
`

const HeroSection = styled.section`
  padding-top: 100px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%);
    z-index: 0;
  }

  @media (max-width: 800px) {
    padding-top: 80px;
    padding-bottom: 40px;
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 0 20px;
  }
`

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const ProductLogo = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 800px) {
    font-size: 40px;
  }
`

const HeroTagline = styled.p`
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 30px;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
`

const CTAButton = styled.button`
  background: #ffffff;
  color: #10b981;
  font-size: 18px;
  font-weight: 700;
  padding: 18px 50px;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  transition: all 500ms ease-in-out;

  &:hover {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: #FFD93D;
    color: #2d3748;
  }

  @media (max-width: 800px) {
    width: 100%;
    padding: 18px 40px;
  }
`

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 800px) {
    padding: 60px 0;
  }
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

const FeatureCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(16, 185, 129, 0.1);
  transition: all 500ms ease-in-out;
  text-align: center;

  &:hover {
    transform: translate3d(0, -10px, 0);
    box-shadow: 0px 12px 40px rgba(16, 185, 129, 0.2);
  }
`

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 25px;
`

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
`

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const BenefitsSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
`

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const BenefitItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`

const BenefitIcon = styled.div`
  font-size: 32px;
  color: #10b981;
  flex-shrink: 0;
`

const BenefitContent = styled.div``

const BenefitTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const HowItWorksSection = styled(Section)`
  background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 20px rgba(16, 185, 129, 0.1);
`

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 auto 20px;
`

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4a5568;
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const CTADescription = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtonWhite = styled(CTAButton)`
  background: white;
  color: #10b981;

  &:hover {
    background: #FFD93D;
    color: #2d3748;
  }
`

const ZenoHire = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: '🤖',
      title: 'AI-Powered Candidate Matching',
      description: 'Advanced machine learning algorithms analyze resumes and job descriptions to find the perfect candidate-job fit, saving time and improving hiring quality.'
    },
    {
      icon: '⚡',
      title: 'Automated Resume Screening',
      description: 'Instantly screen thousands of applications with intelligent AI that identifies top candidates based on skills, experience, and cultural fit.'
    },
    {
      icon: '📊',
      title: 'Smart Analytics & Insights',
      description: 'Get actionable insights into your recruitment pipeline with real-time analytics, performance metrics, and predictive hiring trends.'
    },
    {
      icon: '🎯',
      title: 'Intelligent Job Posting',
      description: 'Optimize job descriptions and automatically post to multiple platforms with AI-generated content that attracts top talent.'
    },
    {
      icon: '💬',
      title: 'AI Chatbot Assistant',
      description: 'Engage candidates 24/7 with an intelligent chatbot that answers questions, schedules interviews, and provides instant feedback.'
    },
    {
      icon: '🔗',
      title: 'Seamless ATS Integration',
      description: 'Integrate effortlessly with your existing Applicant Tracking System and HR tools for a unified recruitment workflow.'
    }
  ]

  const benefits = [
    {
      icon: '⏱️',
      title: 'Reduce Time-to-Hire by 70%',
      description: 'Streamline your recruitment process with automation and AI, drastically reducing the time from job posting to offer acceptance.'
    },
    {
      icon: '💎',
      title: 'Improve Quality of Hire',
      description: 'Find better-matched candidates with AI-driven insights that go beyond keywords to assess true fit and potential.'
    },
    {
      icon: '💰',
      title: 'Lower Recruitment Costs',
      description: 'Reduce spending on job boards, agencies, and manual screening while increasing hiring efficiency and ROI.'
    },
    {
      icon: '🎓',
      title: 'Eliminate Unconscious Bias',
      description: 'AI-powered screening focuses on skills and qualifications, promoting diversity and reducing human bias in hiring decisions.'
    },
    {
      icon: '📈',
      title: 'Scale Your Hiring',
      description: 'Handle high-volume recruitment effortlessly, from a few positions to hundreds, without compromising quality or speed.'
    },
    {
      icon: '🌟',
      title: 'Enhanced Candidate Experience',
      description: 'Provide timely communication, personalized interactions, and a smooth application process that strengthens your employer brand.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Post Your Job',
      description: 'Create optimized job descriptions with AI assistance and publish across multiple platforms instantly.'
    },
    {
      number: '2',
      title: 'AI Screens Candidates',
      description: 'Our intelligent system automatically reviews applications and ranks candidates by fit and qualification.'
    },
    {
      number: '3',
      title: 'Review Top Matches',
      description: 'Access a curated shortlist of the best candidates with detailed AI-generated insights and recommendations.'
    },
    {
      number: '4',
      title: 'Hire with Confidence',
      description: 'Interview and select from pre-qualified candidates, making faster, data-driven hiring decisions.'
    }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <ProductLogo>Zeno hire</ProductLogo>
            <HeroTagline>Transform Your Hiring with AI Intelligence</HeroTagline>
            <HeroDescription>
              Zeno hire is an advanced AI-powered recruitment platform that revolutionizes how companies find,
              screen, and hire top talent. Leverage cutting-edge artificial intelligence to make smarter hiring
              decisions faster and more efficiently than ever before.
            </HeroDescription>
            <CTAButton onClick={() => navigate('/contact')}>
              Request a Demo
            </CTAButton>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <SectionTitle>The Future of Recruitment is Here</SectionTitle>
          <SectionSubtitle>
            Zeno hire combines the power of artificial intelligence with human expertise to create
            a recruitment platform that's faster, smarter, and more effective. Say goodbye to manual
            resume screening and hello to data-driven hiring decisions that transform your talent acquisition.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <BenefitsSection>
        <MaxWidthContainer>
          <SectionTitle>Why Leading Companies Choose Zeno hire</SectionTitle>
          <SectionSubtitle>
            Join innovative organizations that are transforming their recruitment process with AI-powered
            automation, delivering measurable results and competitive advantages in the war for talent.
          </SectionSubtitle>

          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </MaxWidthContainer>
      </BenefitsSection>

      <HowItWorksSection>
        <MaxWidthContainer>
          <SectionTitle>How Zeno hire Works</SectionTitle>
          <SectionSubtitle>
            Our AI-powered platform simplifies every step of the recruitment process, from posting to hiring.
          </SectionSubtitle>

          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsGrid>
        </MaxWidthContainer>
      </HowItWorksSection>

      <CTASection>
        <MaxWidthContainer>
          <CTATitle>Ready to Revolutionize Your Hiring Process?</CTATitle>
          <CTADescription>
            Join hundreds of forward-thinking companies using Zeno hire to build exceptional teams faster.
            Schedule a personalized demo and see how AI can transform your recruitment strategy today.
          </CTADescription>
          <CTAButtonWhite onClick={() => navigate('/contact')}>
            Schedule Your Demo
          </CTAButtonWhite>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default ZenoHire
